import { z } from 'zod';

export const SsoProtocolSchema = z.enum(['saml', 'oidc']);

export const SsoConnectionSchema = z.object({
  workspace_id: z.string().min(1),
  protocol: SsoProtocolSchema,
  provider_id: z.string().min(1), // 'saml.<wid>' | 'oidc.<wid>'
  display_name: z.string().min(1),
  status: z.enum(['draft', 'active', 'disabled']),
  created_at_ms: z.number().int().nonnegative(),
  updated_at_ms: z.number().int().nonnegative(),
  // Non-secret IdP config persisted so the admin form redisplays what was
  // configured. The OIDC client_secret and SAML certificates are NEVER stored
  // here — they live only in the identity platform.
  oidc: z
    .object({
      issuer: z.string(),
      client_id: z.string(),
    })
    .optional(),
  saml: z
    .object({
      idp_entity_id: z.string(),
      idp_metadata_url: z.string().optional(),
    })
    .optional(),
});

// Result of a "Test connection" run: a set of named checks the admin can act on.
export const SsoConnectionTestSchema = z.object({
  ok: z.boolean(),
  checks: z.array(
    z.object({
      name: z.string(),
      ok: z.boolean(),
      detail: z.string(),
    }),
  ),
});

const SamlCreateSchema = z.object({
  protocol: z.literal('saml'),
  display_name: z.string().min(1),
  saml: z.object({
    idp_entity_id: z.string().min(1),
    // Provide either a metadata URL or pasted XML; the route validates one is present.
    idp_metadata_url: z.string().url().optional(),
    idp_metadata_xml: z.string().min(1).optional(),
  }),
});

const OidcCreateSchema = z.object({
  protocol: z.literal('oidc'),
  display_name: z.string().min(1),
  oidc: z.object({
    issuer: z.string().url(),
    client_id: z.string().min(1),
    client_secret: z.string().min(1),
  }),
});

export const CreateSsoConnectionSchema = z.discriminatedUnion('protocol', [
  SamlCreateSchema,
  OidcCreateSchema,
]);

export const SsoDomainSchema = z.object({
  domain: z.string().min(1),
  workspace_id: z.string().min(1),
  provider_id: z.string().min(1),
  verified_at_ms: z.number().int().nonnegative(),
});

export const ResolveSsoResponseSchema = z.object({
  provider_id: z.string().min(1).nullable(),
});

export const SsoDomainsListSchema = z.array(SsoDomainSchema);
export const ClaimDomainResponseSchema = z.object({ domain: z.string(), txt_record: z.string() });

export type ParsedSsoConnection = z.infer<typeof SsoConnectionSchema>;
export type ParsedSsoConnectionTest = z.infer<typeof SsoConnectionTestSchema>;
export type ParsedCreateSsoConnection = z.infer<typeof CreateSsoConnectionSchema>;
export type ParsedSsoDomain = z.infer<typeof SsoDomainSchema>;
export type ParsedResolveSso = z.infer<typeof ResolveSsoResponseSchema>;
export type ParsedClaimDomain = z.infer<typeof ClaimDomainResponseSchema>;
